/**
 * @module scroll-effects
 * Handles scroll-triggered behaviors using IntersectionObserver.
 * It watches elements with `data-scroll-call` attributes and fires
 * corresponding functions when they enter or leave the viewport.
 */

let observer;

/**
 * Handles changing the body's background color attribute based on section visibility.
 * When a section with `data-scroll-call="sectionBgColour"` enters the view,
 * it sets `data-bgcolor` on the `<body>` element using the value from the
 * section's `data-style-set` attribute.
 *
 * @param {string} way - 'enter' or 'exit'.
 * @param {HTMLElement} el - The section element.
 */
function sectionBgColour(way, el) {
    if (way === 'enter') {
        const styleSet = el.getAttribute('data-style-set');
        if (styleSet) {
            document.body.setAttribute('data-bgcolor', styleSet);
        }
    }
    // Note: On 'exit', we don't remove the attribute. The 'enter' event of the
    // next section will overwrite it, which provides a smooth transition.
}

/**
 * Toggles the 'is-inview' class on elements when they enter or leave the viewport.
 * This is typically used for triggering CSS animations or transitions.
 *
 * @param {string} way - 'enter' or 'exit'.
 * @param {HTMLElement} el - The layout element.
 */
function layoutInView(way, el) {
    if (way === 'enter') {
        el.classList.add('is-inview');
    }
}

// A map of function names (from data-scroll-call) to the actual functions.
const scrollCallFunctions = {
    sectionBgColour,
    layoutInView,
};

/**
 * The callback for the IntersectionObserver.
 * @param {IntersectionObserverEntry[]} entries - The entries reported by the observer.
 */
function observerCallback(entries) {
    entries.forEach(entry => {
        const funcs = entry.target.getAttribute('data-scroll-call').split(' ');
        const way = entry.isIntersecting ? 'enter' : 'exit';

        funcs.forEach(funcName => {
            if (scrollCallFunctions[funcName]) {
                scrollCallFunctions[funcName](way, entry.target);
            }
        });
    });
}

/**
 * Initializes the scroll effects by setting up the IntersectionObserver.
 * This should be called once per page load or Barba transition.
 */
export function initializeScrollEffects() {
    if (observer) observer.disconnect();

    // The threshold determines what percentage of the target is visible for the callback to be invoked.
    // A value of 0.1 means the event fires when 10% of the element is visible.
    // The original snippet used 0.5, but a lower value often provides a better user experience for animations.
    const observerOptions = {
        threshold: 0.1,
    };

    observer = new IntersectionObserver(observerCallback, observerOptions);

    const elementsToObserve = document.querySelectorAll('[data-scroll-call]');
    elementsToObserve.forEach(el => observer.observe(el));
}

/**
 * Cleans up by disconnecting the IntersectionObserver.
 * This is crucial to call before a page transition to prevent memory leaks.
 */
export function destroyScrollEffects() {
    if (observer) {
        observer.disconnect();
        observer = null;
    }
}